import { Button, DialogV2, Typography } from '@recruit-robin/robin-theme/web-components';
import { XClose } from '@recruit-robin/robin-theme/web-icons';
import { memo, useContext, useEffect, useRef, useState } from 'react';

import type { EnrichVacancyModalContextProps } from './components/EnrichVacancyModalContext/EnrichVacancyModalContext.types';

import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useFeatureToggles } from 'shared/legacy/hooks/useFeatureToggles';
import { useGetJobImprovedQuery } from 'shared/legacy/hooks/useGetJobImprovedQuery';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useIsGlobalDialogOpen } from 'stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { useUnsavedChanges } from 'stores/unsaved-changes/hooks/useUnsavedChanges';
import { UnsavedChangesArea } from 'stores/unsaved-changes/unsaved-changes.types';
import {
  EditEnrichedVacancyContext,
  editEnrichedVacancyContextInitialData,
} from './components/EditEnrichedVacancyForm/EditEnrichedVacancyContext/EditEnrichedVacancyContext';
import { EditEnrichedVacancyContextState } from './components/EditEnrichedVacancyForm/EditEnrichedVacancyContext/EditEnrichedVacancyContext.types';
import { EnrichedVersionRef } from './components/EditEnrichedVacancyForm/EnrichedVersion/EnrichedVersion.types';
import { JobPreviewRef } from './components/EditEnrichedVacancyForm/JobPreview/JobPreview.types';
import {
  EnrichVacancyModalContext,
  enrichVacancyModalContextInitialData,
} from './components/EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyPages } from './components/EnrichVacancyPages/EnrichVacancyPages';
import { SubmitButton } from './components/SubmitButton/SubmitButton';
import { VacancySideList } from './components/VacancySideList/VacancySideList';
import { useStyles } from './EnrichVacancyModal.styles';
import { useSetupModalWhenVisible } from './hooks/useSetupModalWhenVisible';
import { mapJobImprovedGetDetailOutputToJobFormValues } from './utils/EnrichVacancyModal.mappers';

const EnrichVacancyModalBase = memo(() => {
  const { dictionary } = useContext(LocalizationContext);
  const isVisible = useIsGlobalDialogOpen(GlobalDialogType.EnrichVacancy);
  const enrichVacancyModal = useGlobalDialog(GlobalDialogType.EnrichVacancy);
  const classes = useStyles();

  const { isFeatureToggleEnabled } = useFeatureToggles();
  const { withNavigationGuard } = useUnsavedChanges([
    UnsavedChangesArea.EnrichVacancyModal_JobPreview,
    ...(isFeatureToggleEnabled((f) => f.RJ_COMMUNICATION_HUB)
      ? [UnsavedChangesArea.EnrichVacancyModal_EnrichedVersion]
      : []),
  ]);

  const handleClose = () => {
    withNavigationGuard(() => {
      enrichVacancyModal.close();
    });
  };

  return (
    <DialogV2.Root customCss={classes.root} isVisible={isVisible}>
      <DialogV2.Backdrop />

      <DialogV2.Content customCss={classes.modal}>
        <DialogV2.Header customCss={classes.title}>
          <Typography variant={(v) => v.heading[3]} color={(color) => color.neutral.variant[70]}>
            {dictionary.manageYourJobs}
          </Typography>
          <div css={classes.titleActions}>
            <Button
              style="text"
              type="subtle2"
              onClick={handleClose}
              label={dictionary.close}
              startIcon={<XClose size={16} />}
            />

            <SubmitButton />
          </div>
        </DialogV2.Header>
        <DialogV2.Body customCss={classes.content}>
          <VacancySideList />

          <EnrichVacancyPages />
        </DialogV2.Body>
      </DialogV2.Content>
    </DialogV2.Root>
  );
});

export const EnrichVacancyModal = memo(() => {
  const [state, setState] = useState(enrichVacancyModalContextInitialData.state);
  const [vacancy, setVacancy] = useState(enrichVacancyModalContextInitialData.vacancy);
  const [modalType, setModalType] = useState(enrichVacancyModalContextInitialData.modalType);
  const [editVacancyTab, setEditVacancyTab] = useState(enrichVacancyModalContextInitialData.editVacancyTab);

  const enrichVacancyModalContextValue: EnrichVacancyModalContextProps = {
    state,
    modalType,
    vacancy,
    editVacancyTab,

    setVacancy,
    setState,
    setEditVacancyTab,
  };

  const { data: jobImproved } = useGetJobImprovedQuery(vacancy?.db_id);

  const [enrichedVersionInitialData, setEnrichedVersionInitialData] = useState(
    editEnrichedVacancyContextInitialData.enrichedVersion.initialData,
  );
  const [jobPreviewInitialData, setJobPreviewInitialData] = useState(
    editEnrichedVacancyContextInitialData.jobPreview.initialData,
  );
  const [enrichedVersionHasErrors, setEnrichedVersionHasErrors] = useState(
    editEnrichedVacancyContextInitialData.enrichedVersion.hasErrors,
  );

  const enrichedVersionRef = useRef<EnrichedVersionRef>(null);
  const jobPreviewRef = useRef<JobPreviewRef>(null);

  useEffect(() => {
    if (jobImproved) {
      const jobFormValues = mapJobImprovedGetDetailOutputToJobFormValues(jobImproved);

      setEnrichedVersionInitialData(jobFormValues);
      setJobPreviewInitialData(jobImproved.job_preview);
    }
  }, [jobImproved]);

  const editEnrichedVacancyContextValue: EditEnrichedVacancyContextState = {
    enrichedVersion: {
      ref: enrichedVersionRef,
      initialData: enrichedVersionInitialData,
      hasErrors: enrichedVersionHasErrors,
    },
    jobPreview: { ref: jobPreviewRef, initialData: jobPreviewInitialData },

    setEnrichedVersionInitialData,
    setJobPreviewInitialData,
    setEnrichedVersionHasErrors,
  };

  useSetupModalWhenVisible({ setVacancy, setState, setModalType, setEditVacancyTab });

  return (
    <EnrichVacancyModalContext.Provider value={enrichVacancyModalContextValue}>
      <EditEnrichedVacancyContext.Provider value={editEnrichedVacancyContextValue}>
        <EnrichVacancyModalBase />
      </EditEnrichedVacancyContext.Provider>
    </EnrichVacancyModalContext.Provider>
  );
});
